import { Button } from '@/components/button';
import { setTotalRows } from '@/reducers/helpers';
import { clearErrors } from '@/reducers/ots';
import { AppDispatch } from '@/root/store';
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  TrashIcon,
} from '@heroicons/react/24/solid';
import React, { ChangeEvent, useRef, useState } from 'react';
import { DropzoneOptions } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';

interface ExcelData {
  headers: string[];
  rows: (string | number | boolean | null)[][];
  sheetNames: string[];
  fileName: string;
  activeSheet: string;
}

interface ExcelPreviewState {
  data: ExcelData | null;
  loading: boolean;
  error: string;
}

interface DropzoneProps
  extends Pick<
    DropzoneOptions,
    'accept' | 'minSize' | 'maxSize' | 'maxFiles' | 'disabled'
  > {
  onChange: (file: File) => void;
  rowErrors: any;
  onDelete?: () => void;
  previewUrl?: string;
  type?: string;
  mimeType?: string;
  maxSizeMb?: number;
  templateUrl?: string;
}

const ExcelUploadForm: React.FC<DropzoneProps> = ({ onChange, rowErrors }) => {
  const dispatch: AppDispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<ExcelPreviewState>({
    data: null,
    loading: false,
    error: '',
  });

  const { event } = useSelector((state: any) => state.event);

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (!file) return;

    onChange(file);

    // Validate file type
    if (!file.name.match(/\.(xlsx|xls)$/)) {
      setState((prev) => ({
        ...prev,
        error: 'Please select a valid Excel file (.xlsx or .xls)',
        data: null,
      }));
      return;
    }

    setState((prev) => ({
      ...prev,
      loading: true,
      error: '',
    }));

    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>): void => {
      try {
        const result = e.target?.result;
        if (!result) {
          throw new Error('Failed to read file');
        }

        const workbook = XLSX.read(result, { type: 'binary' });
        const sheetList = workbook.SheetNames;

        if (sheetList.length > 0) {
          const firstSheetData = loadSheet(workbook, sheetList[0]);
          setState((prev) => ({
            ...prev,
            data: {
              ...firstSheetData,
              sheetNames: sheetList,
              fileName: file.name,
              activeSheet: sheetList[0],
            },
            loading: false,
          }));

          dispatch(setTotalRows(firstSheetData.rows.length));
        } else {
          throw new Error('No sheets found in the Excel file');
        }
      } catch (err) {
        setState((prev) => ({
          ...prev,
          error: `Error reading file: ${err instanceof Error ? err.message : 'Unknown error'}`,
          loading: false,
          data: null,
        }));
      }
    };

    reader.onerror = (): void => {
      setState((prev) => ({
        ...prev,
        error: 'Error reading file',
        loading: false,
        data: null,
      }));
    };

    reader.readAsBinaryString(file);
  };

  const loadSheet = (
    workbook: XLSX.WorkBook,
    sheetName: string
  ): Pick<ExcelData, 'headers' | 'rows'> => {
    const worksheet = workbook.Sheets[sheetName];
    const jsonData: (string | number | boolean | null)[][] =
      XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: null,
      });

    if (jsonData.length > 0) {
      const headers = (jsonData[0] || []).map(
        (header, index) => header?.toString() || `Column ${index + 1}`
      );
      const rows = jsonData.slice(1) || [];
      return { headers, rows };
    }

    return { headers: [], rows: [] };
  };

  const handleSheetChange = (sheetName: string): void => {
    if (!state.data) return;

    // Re-read the file to get the workbook for sheet switching
    const fileInput = document.querySelector(
      'input[type="file"]'
    ) as HTMLInputElement;
    const file = fileInput?.files?.[0];

    if (file) {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>): void => {
        try {
          const result = e.target?.result;
          if (!result) return;

          const workbook = XLSX.read(result, { type: 'binary' });
          const sheetData = loadSheet(workbook, sheetName);

          setState((prev) => ({
            ...prev,
            data: prev.data
              ? {
                  ...prev.data,
                  ...sheetData,
                  activeSheet: sheetName,
                }
              : null,
          }));
        } catch (err) {
          setState((prev) => ({
            ...prev,
            error: `Error loading sheet: ${err instanceof Error ? err.message : 'Unknown error'}`,
          }));
        }
      };
      reader.readAsBinaryString(file);
    }
  };

  const resetPreview = (): void => {
    setState({
      data: null,
      loading: false,
      error: '',
    });

    dispatch(clearErrors());

    const fileInput = document.querySelector(
      'input[type="file"]'
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }

    dispatch(setTotalRows(0));
  };

  const renderCell = (
    value: string | number | boolean | null,
    rowIndex: number,
    colIndex: number
  ): string => {
    if (value === null || value === undefined) return '';
    return String(value);
  };

  const handleUploadClick = (): void => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <div className='mb-6'>
        {/* File Upload */}
        <div className='flex flex-col items-center gap-4 rounded-md border-[1px] border-neutral-40 bg-neutral-20 p-4'>
          <p className='text-xs text-neutral-70'>
            Maksimal ukuran file 2MB & Maksimal 2 file (Format: .xlsx, .xls)
          </p>
          <div className='flex justify-center gap-2'>
            <Button
              size='sm'
              element='button'
              onClick={handleUploadClick}
              leftIcon={<ArrowUpTrayIcon />}
            >
              Unggah
            </Button>
            <Button
              variant={'neutral'}
              size='sm'
              element='a'
              target={'_blank'}
              href={event.bulk_checkout_template_url}
              leftIcon={<ArrowDownTrayIcon />}
            >
              Download Template
            </Button>
          </div>

          <input
            ref={fileInputRef}
            type='file'
            accept='.xlsx,.xls'
            onChange={handleFileUpload}
            className='hidden'
          />
        </div>

        {/* Error Message */}
        {state.error && (
          <div className='mt-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4'>
            <span className='text-red-700'>{state.error}</span>
          </div>
        )}

        {/* Loading */}
        {state.loading && (
          <div className='mt-4 text-center'>
            <div className='inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600'></div>
            <p className='mt-2 text-gray-600'>Loading file...</p>
          </div>
        )}
      </div>

      {/* File Info and Controls */}
      {state.data && !state.loading && (
        <div className='mb-6 rounded-lg bg-blue-50 p-4'>
          <div className='flex flex-wrap items-center justify-between gap-4'>
            <div>
              <p className='font-medium text-gray-800'>
                File: {state.data.fileName}
              </p>
              <p className='text-sm text-gray-600'>
                {state.data.rows.length} rows, {state.data.headers.length}{' '}
                columns
              </p>
            </div>

            <div className='flex gap-2'>
              {/* Sheet Selector */}
              {state.data.sheetNames.length > 1 && (
                <select
                  value={state.data.activeSheet}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    handleSheetChange(e.target.value)
                  }
                  className='rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500'
                >
                  {state.data.sheetNames.map((sheet: string) => (
                    <option key={sheet} value={sheet}>
                      {sheet}
                    </option>
                  ))}
                </select>
              )}

              <Button
                size='sm'
                variant='danger'
                look='outline'
                element='button'
                onClick={resetPreview}
                leftIcon={<TrashIcon />}
              >
                Reset
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Data Preview Table */}
      {state.data && state.data.headers.length > 0 && (
        <div className='overflow-hidden rounded-lg border border-gray-200'>
          <div className='max-h-72 overflow-x-auto overflow-y-auto'>
            <table className='min-w-full divide-y divide-gray-200'>
              <thead className='sticky top-0 bg-gray-50'>
                <tr>
                  <th className='border-r border-gray-200 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                    #
                  </th>
                  {state.data.headers.map((header: string, index: number) => (
                    <th
                      key={index}
                      className='border-r border-gray-200 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 last:border-r-0'
                    >
                      {header}
                    </th>
                  ))}
                  <th className='px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-200 bg-white'>
                {state.data.rows.map(
                  (
                    row: (string | number | boolean | null)[],
                    rowIndex: number
                  ) => {
                    const rowNumber = rowIndex + 2;
                    const hasErrors =
                      rowErrors[rowNumber] && rowErrors[rowNumber].length > 0;

                    return (
                      <tr key={rowIndex} className='hover:bg-gray-50'>
                        <td className='border-r border-gray-200 px-4 py-3 text-sm font-medium text-gray-500'>
                          {rowIndex + 1}
                        </td>
                        {state.data!.headers.map((_, colIndex: number) => (
                          <td
                            key={colIndex}
                            className='max-w-xs truncate border-r border-gray-200 px-4 py-3 text-sm text-gray-900 last:border-r-0'
                            title={renderCell(
                              row[colIndex],
                              rowIndex,
                              colIndex
                            )}
                          >
                            {renderCell(row[colIndex], rowIndex, colIndex)}
                          </td>
                        ))}
                        <td className='px-4 py-3 text-sm'>
                          {hasErrors ? (
                            <div>
                              <span className='inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800'>
                                Error
                              </span>
                              <div className='mt-1 text-xs text-red-600'>
                                {rowErrors[rowNumber].join(', ')}
                              </div>
                            </div>
                          ) : (
                            <span className='inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800'>
                              OK
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  }
                )}
              </tbody>
            </table>
          </div>

          {state.data.rows.length > 100 && (
            <div className='border-t border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600'>
              Showing first 100 rows of {state.data.rows.length} total rows
            </div>
          )}

          {state.data.rows.length === 0 && (
            <div className='px-4 py-8 text-center text-gray-500'>
              No data found in this sheet
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ExcelUploadForm;
